import React, { useEffect, useState } from "react";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPaperPlane,
} from "react-icons/fa";
import Footer from "../Footer/Footer";
import Navbar from "../Navbar/Navbar";

const ContactUs = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email))
      newErrors.email = "Invalid email address";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    else if (formData.message.trim().length < 10)
      newErrors.message = "Message must be at least 10 characters";
    return newErrors;
  };

  useEffect(() => window.scrollTo({ top: 0, behavior: "smooth" }), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      alert("Message sent successfully!");
      setFormData({ name: "", email: "", message: "" });
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      id: 1,
      icon: <FaPhoneAlt />,
      title: "Call Us",
      content: "+91 9266300673",
      description: "Mon–Fri, 10:00AM–7:00PM IST",
      color: "#286a94",
    },
    {
      id: 2,
      icon: <FaEnvelope />,
      title: "Email Us",
      content: "support@ebench.ai",
      description: "We usually reply within 24 hours",
      color: "#1e4e70",
    },
    {
      id: 3,
      icon: <FaMapMarkerAlt />,
      title: "Visit Us",
      content: "H-15, Sector 63, Noida, India",
      description: "Uttar Pradesh 201301",
      color: "#307aa6",
    },
  ];

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        {/* ===== HERO SECTION ===== */}
        <section style={styles.heroSection}>
          <div style={styles.heroOverlay}></div>
          <div style={styles.heroContent}>
            <div style={styles.heroBadge}>
              <FaPaperPlane style={styles.heroBadgeIcon} />
              <span>We’re Here to Help</span>
            </div>
            <h1 style={styles.heroTitle}>Contact Us</h1>
            <p style={styles.heroSubtitle}>
              Have questions or need assistance? Our team is ready to help you.
            </p>
          </div>
        </section>

        {/* ===== CONTACT SECTION ===== */}
        <section style={styles.mainSection}>
          <div style={styles.mainContainer}>
            <div className="content-grid" style={styles.contentGrid}>
              {/* LEFT SIDE */}
              <div style={styles.leftSide}>
                <h2 style={styles.sectionTitle}>Let’s Connect</h2>
                <p style={styles.sectionText}>
                  Whether you have a question about features, pricing, or
                  anything else, we’re here to help.
                </p>

                <div style={styles.infoCards}>
                  {contactInfo.map((item) => (
                    <div key={item.id} style={styles.infoCard}>
                      <div
                        style={{
                          ...styles.iconBox,
                          background: item.color,
                        }}
                      >
                        {item.icon}
                      </div>
                      <div>
                        <h3 style={styles.infoTitle}>{item.title}</h3>
                        <p style={styles.infoContent}>{item.content}</p>
                        <p style={styles.infoDesc}>{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT SIDE (FORM) */}
              <form onSubmit={handleSubmit} style={styles.formCard}>
                <h2 style={styles.formTitle}>Send a Message</h2>
                <p style={styles.formSubtitle}>
                  Fill out the form below and we’ll get back to you soon.
                </p>

                <div style={styles.formGroup}>
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    style={styles.input}
                  />
                  {errors.name && (
                    <span style={styles.errorText}>{errors.name}</span>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <input
                    type="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    style={styles.input}
                  />
                  {errors.email && (
                    <span style={styles.errorText}>{errors.email}</span>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <textarea
                    placeholder="Your Message"
                    rows="5"
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    style={{ ...styles.input, ...styles.textarea }}
                  />
                  {errors.message && (
                    <span style={styles.errorText}>{errors.message}</span>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    ...styles.submitButton,
                    opacity: isSubmitting ? 0.7 : 1,
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div style={styles.spinner}></div> Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane style={styles.buttonIcon} /> Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

/* ===== STYLES ===== */
const styles = {
  container: { backgroundColor: "#f8fbff", minHeight: "100vh", overflowX: "hidden" },

  heroSection: {
    background: "linear-gradient(135deg, #286a94, #1e4e70)",
    color: "#fff",
    padding: "4rem 1rem",
    textAlign: "center",
    position: "relative",
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle at 50% 20%, rgba(255,255,255,0.1), transparent 70%)",
  },
  heroContent: { position: "relative", zIndex: 1, padding: "0 1rem" },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    background: "rgba(255,255,255,0.15)",
    borderRadius: "2rem",
    padding: "0.5rem 1.25rem",
    fontSize: "0.85rem",
    fontWeight: "500",
    marginBottom: "1.2rem",
  },
  heroBadgeIcon: { color: "#fbd46d" },
  heroTitle: {
    fontSize: "clamp(2rem, 6vw, 3rem)",
    fontWeight: "700",
    marginBottom: "0.8rem",
  },
  heroSubtitle: {
    color: "#e0e7ff",
    fontSize: "clamp(1rem, 3vw, 1.25rem)",
    maxWidth: "600px",
    margin: "0 auto",
  },

  mainSection: { padding: "4rem 1rem" },
  mainContainer: { maxWidth: "1200px", margin: "0 auto" },

  // 🔧 Responsive grid
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "2rem",
  },

  sectionTitle: { color: "#1e4e70", fontSize: "1.75rem", fontWeight: 700 },
  sectionText: { color: "#475569", marginBottom: "1.5rem", lineHeight: 1.6 },
  infoCards: { display: "flex", flexDirection: "column", gap: "1.25rem" },
  infoCard: {
    display: "flex",
    gap: "1rem",
    alignItems: "flex-start",
    background: "#fff",
    borderRadius: "1rem",
    padding: "1.25rem",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
  },
  iconBox: {
    width: "3rem",
    height: "3rem",
    borderRadius: "0.75rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "1.1rem",
  },
  infoTitle: { fontSize: "1.1rem", color: "#1e4e70", fontWeight: 600 },
  infoContent: { fontWeight: 500, color: "#1f2937" },
  infoDesc: { color: "#6b7280", fontSize: "0.9rem" },

  formCard: {
    background: "#fff",
    padding: "1.5rem",
    borderRadius: "1rem",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  },
  formTitle: { color: "#1e4e70", fontSize: "1.5rem", fontWeight: 700 },
  formSubtitle: { color: "#475569", marginBottom: "1.2rem" },
  formGroup: { display: "flex", flexDirection: "column", marginBottom: "1rem" },
  input: {
    width: "100%",
    padding: "0.85rem",
    borderRadius: "0.75rem",
    border: "1px solid #cbd5e1",
    background: "#f8fbff",
    fontSize: "1rem",
  },
  textarea: { minHeight: "120px", resize: "none" },
  errorText: { color: "red", fontSize: "0.85rem" },
  submitButton: {
    background: "linear-gradient(135deg, #286a94, #1e4e70)",
    color: "#fff",
    border: "none",
    borderRadius: "0.75rem",
    padding: "0.9rem 1.5rem",
    fontWeight: 600,
    fontSize: "1rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  buttonIcon: { fontSize: "1.125rem" },
  spinner: {
    width: 20,
    height: 20,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid white",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
};

const styleSheet = document.createElement("style");
styleSheet.innerHTML = `
  @media (max-width: 900px) {
    .content-grid {
      grid-template-columns: 1fr !important;
      gap: 2rem !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default ContactUs;
